import { useEffect, useRef, useState } from 'react'
import { FileText, Eye } from 'lucide-react'
import api from '@/lib/api'
import { Badge } from '@/components/ui/badge'
import { Card } from '@/components/ui/card'
import { formatBRL, formatCnpj, formatDate } from '@/lib/utils'

/**
 * Card somente-leitura com os dados do contrato selecionado na NF: serviços com
 * fatura ativa (quantidade, valor unitário e total) e as demais informações do
 * contrato para conferência enquanto a NF é preenchida. Ver docs/NF.md.
 *
 * `onTotalServicos` devolve ao formulário o total faturável do contrato (ou
 * null quando indisponível), que alimenta o indicador de valor divergente.
 */
export default function ContratoResumoCard({ contratoID, onTotalServicos }) {
  const [dados, setDados] = useState(null)
  const [loading, setLoading] = useState(false)
  const [erro, setErro] = useState('')

  // Ref: o callback não precisa ser estável no componente pai.
  const avisarTotal = useRef(onTotalServicos)
  avisarTotal.current = onTotalServicos

  useEffect(() => {
    if (!contratoID) {
      setDados(null)
      avisarTotal.current?.(null)
      return
    }
    let ativo = true
    setLoading(true)
    setErro('')
    api.get(`/contratos/${contratoID}/resumo`)
      // Sem serviço marcado para fatura não há base de comparação: envia null.
      .then(({ data }) => { if (ativo) { setDados(data); avisarTotal.current?.(data.servicos?.length ? data.totalServicos : null) } })
      .catch(() => { if (ativo) { setDados(null); setErro('Não foi possível carregar os dados do contrato.'); avisarTotal.current?.(null) } })
      .finally(() => { if (ativo) setLoading(false) })
    return () => { ativo = false }
  }, [contratoID])

  if (!contratoID) return null

  if (loading || erro || !dados) {
    return (
      <Card className="p-5">
        <p className="text-sm text-muted-foreground">
          {erro || 'Carregando dados do contrato...'}
        </p>
      </Card>
    )
  }

  const { contrato: c, servicos, totalServicos } = dados
  const vencendo = c.diasParaAditivar !== null && c.diasParaAditivar <= 90

  return (
    <Card className="p-5">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="font-semibold flex items-center gap-2">
            <FileText className="h-4 w-4 text-muted-foreground" />
            Contrato {c.numeroContrato}
          </h2>
          {c.ativo
            ? <Badge variant="success">Ativo</Badge>
            : <Badge variant="muted">Inativo{c.motivoInativacao ? `: ${c.motivoInativacao}` : ''}</Badge>}
          <Badge variant={c.assinadoCliente ? 'default' : 'warning'}>
            {c.assinadoCliente ? 'Assinado pelo cliente' : 'Não assinado'}
          </Badge>
        </div>
        <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Eye className="h-3.5 w-3.5" /> Somente conferência
        </span>
      </div>

      {/* Cliente */}
      <div className="rounded-lg border border-border bg-muted/30 px-4 py-3 mb-4">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="font-medium">{c.clienteNome}</span>
          <span className="text-muted-foreground">-</span>
          <span className="text-sm text-muted-foreground tabular-nums">{formatCnpj(c.clienteCnpj)}</span>
          <span className="text-muted-foreground">-</span>
          <span className="text-sm text-muted-foreground">
            {c.municipioNome}{c.estadoUf ? `/${c.estadoUf}` : ''}
          </span>
          {c.clienteEmail && (
            <>
              <span className="text-muted-foreground">-</span>
              <span className="text-sm text-muted-foreground">{c.clienteEmail}</span>
            </>
          )}
        </div>
      </div>

      {/* Dados do contrato */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-3 mb-5">
        <Info label="Modalidade" value={`${c.modalidadeNome} nº ${c.numeroModalidade}`} />
        <Info label="Processo" value={c.numeroProcesso} />
        <Info label="Assinatura" value={formatDate(c.dataContrato)} />
        <Info label="Índice de reajuste" value={c.indiceReajusteNome} />
        <Info label="Vigência inicial" value={formatDate(c.dataVigenciaInicial)} />
        <Info
          label="Vigência até"
          value={
            <span className="flex items-baseline gap-1.5">
              {formatDate(c.dataFimVigente)}
              {c.diasParaAditivar !== null && (
                <span className={vencendo ? 'text-xs text-amber-600' : 'text-xs text-muted-foreground'}>
                  ({c.diasParaAditivar} dias)
                </span>
              )}
            </span>
          }
        />
        <Info label="Valor total do contrato" value={<span className="tabular-nums">{formatBRL(c.valorTotal)}</span>} />
        <Info label="Total dos serviços faturáveis" value={<span className="tabular-nums font-medium">{formatBRL(totalServicos)}</span>} />
        {c.descricao && <Info label="Descrição" value={c.descricao} className="col-span-2 md:col-span-4" />}
      </div>

      {/* Serviços com fatura ativa */}
      <h3 className="text-sm font-semibold mb-2">Serviços com fatura ativa</h3>
      {servicos.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Nenhum serviço marcado como <b className="text-foreground">fatura</b> nos aditivos deste contrato.
        </p>
      ) : (
        <div className="rounded-lg border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-muted-foreground bg-muted/50">
                <tr>
                  <th className="px-3 py-2 font-medium">Serviço</th>
                  <th className="px-3 py-2 font-medium text-right w-20">Qtde</th>
                  <th className="px-3 py-2 font-medium text-right w-36">Valor unitário</th>
                  <th className="px-3 py-2 font-medium text-right w-36">Valor total</th>
                </tr>
              </thead>
              <tbody>
                {servicos.map((s) => (
                  <tr key={s.aditivoServicoID} className="border-t border-border/60 odd:bg-muted/20">
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">{s.servicoNome}</span>
                        {s.servicoTipo && (
                          <Badge variant="muted" className="text-[10px]">{s.servicoTipo}</Badge>
                        )}
                      </div>
                      {(s.servicoDescricao || s.observacao) && (
                        <p className="text-xs text-muted-foreground/80 mt-0.5 whitespace-pre-line break-words">
                          {s.servicoDescricao || s.observacao}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground/60 mt-0.5">
                        Aditivo {s.aditivoTipo} · {formatDate(s.dataInicio)} — {formatDate(s.dataFim)}
                      </p>
                    </td>
                    <td className="px-3 py-2 text-right align-top tabular-nums">{s.quantidade}</td>
                    <td className="px-3 py-2 text-right align-top tabular-nums whitespace-nowrap">{formatBRL(s.valorUnitario)}</td>
                    <td className="px-3 py-2 text-right align-top tabular-nums whitespace-nowrap">{formatBRL(s.valorTotal)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-border bg-muted/30">
                  <td colSpan={3} className="px-3 py-2 text-right text-xs uppercase tracking-wide text-muted-foreground">
                    Total
                  </td>
                  <td className="px-3 py-2 text-right font-semibold tabular-nums whitespace-nowrap">{formatBRL(totalServicos)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </Card>
  )
}

function Info({ label, value, className = '' }) {
  return (
    <div className={className}>
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-sm mt-0.5">{value || '—'}</div>
    </div>
  )
}
